import { RESERVATION_VEHICLE_LIMIT, type FlatPrice, type Parking, type Price, type Vehicle } from '@repo/domain';
import { formatFlatPrice, useUser } from '@repo/domain-service';
import { i18n, tr } from '@repo/locale';
import { type TestId } from '@repo/test-id';
import {
  ParkingHeaderContainer,
  ParkingInformationCard,
  PropertiesCard,
  ReservationCard,
  ReservationPaymentCard,
  VehicleCard,
} from '@repo/entities';
import { InfoCard } from '@repo/ui';
import { useNavigate } from 'react-router-dom';

import ProcessProgress from '@features/reservation/components/process/ProcessProgress';

import HelpButton from '@shared/components/HelpButton';

import { ROUTE_PATHS } from '@router/paths';
import { RouteName } from '@router/routeNames';

interface Props {
  parking: Parking;
  price: Price | undefined;
  // The estimate shown before the reservation was made, set only when it differs from `price`.
  previousFlatPrice: FlatPrice | undefined;
  isPriceError?: boolean;
  testId: TestId;
  onVehicleChange: () => void;
  vehicle: Vehicle;
  vehicles: Vehicle[];
  spotName: string | undefined;
  startAt: Date;
  parkDuration: number;
  secondsToDeadline?: number;
  onExpire?: () => void;
}

export default function SummaryContents({
  parking,
  price,
  previousFlatPrice,
  isPriceError = false,
  testId,
  onVehicleChange,
  vehicle,
  vehicles,
  spotName,
  startAt,
  parkDuration,
  secondsToDeadline,
  onExpire,
}: Props) {
  const { user } = useUser();
  const shouldDisplayProperties = !!parking.properties;
  const navigate = useNavigate();

  const handleGoBack = (): void => {
    navigate(ROUTE_PATHS[RouteName.MAP]);
  };

  return (
    <>
      <div className="flex flex-col gap-6">
        <ProcessProgress
          currentStep={3}
          secondsToDeadline={secondsToDeadline}
          onExpire={onExpire}
          testId={testId.append('progress')}
        />
        <ParkingHeaderContainer
          parking={parking}
          price={price}
          parkDuration={parkDuration}
          parkStartAtTimestamp={startAt.getTime()}
          onGoBack={handleGoBack}
          isPriceError={isPriceError}
          testId={testId.append('parkingDetails')}
        />
      </div>
      {previousFlatPrice && price?.flatPrice && (
        <InfoCard
          color="warning"
          title={tr('reservations:summary.price_changed.title')}
          testId={testId.append('priceChanged')}
        >
          <div className="text-sm">
            {tr('reservations:summary.price_changed.description', {
              previous: formatFlatPrice(previousFlatPrice, i18n.language),
              current: formatFlatPrice(price.flatPrice, i18n.language),
            })}
          </div>
        </InfoCard>
      )}
      <ReservationCard
        testId={testId.append('reservationDetails')}
        startAt={startAt}
        parkDuration={parkDuration}
        spotName={spotName}
        confirmationEmail={user?.email}
      />
      {vehicle && (
        <VehicleCard
          testId={testId.append('vehicle')}
          vehicle={vehicle}
          vehicles={vehicles}
          vehicleLimit={RESERVATION_VEHICLE_LIMIT}
          onVehicleChange={onVehicleChange}
        />
      )}
      {shouldDisplayProperties && (
        <PropertiesCard
          properties={parking.properties}
          testId={testId.append('properties')}
          collapsible
          defaultOpen={false}
          showHelperText
        />
      )}
      <ParkingInformationCard parking={parking} testId={testId.append('parkingInfo')} collapsible defaultOpen={false} />

      {price?.flatPrice && parkDuration && (
        <ReservationPaymentCard
          price={price.flatPrice}
          parkDuration={parkDuration}
          testId={testId.append('payment')}
          collapsible
        />
      )}

      <HelpButton testId={testId.append('help')} />
    </>
  );
}
